import React, { useContext, useEffect, useState } from 'react';
import Price from '../shop/Price';
import CartContext from '../../../contexts/CartContext';
import ShippingClient from '../../../api/ShippingClient';
import { FormControlLabel, Radio } from '@mui/material';
import { CartContents } from '../../../contexts/CartContext';

type Props = {
  items: ICartItem[],
  method?: string,
  name?: string,
  radio?: boolean,
  alwaysVisible?: boolean,
}

// First item with a tenant snapshot wins. Single-tenant carts route correctly
// even when viewed from the other tenant's tab. Mixed-tenant carts pick one
// tenant — shipping accuracy in that case is a separate problem (true mixed-
// cart shipping needs an aggregating endpoint, future work).
const pickCartTenant = (cart: CartContents): string | undefined => {
  for (const item of Object.values(cart.items)) {
    if (item.tenant) return item.tenant;
  }
  return undefined;
};

const Shipping = ({ items, method, radio, alwaysVisible }: Props) => {
  // Get the cart from the context
  const subtotal = items.reduce((subtotal, item) => subtotal + (item.count * (item.item.price ? item.item.price : 0)), 0);
  const {cart, setCartState} = useContext(CartContext);
  const myMethod = method ? method : cart.shippingMethod;
  const [shipping, setShipping] = useState<ShippingDetails|undefined>();

  let discountedSubtotal = cart.discountAmount ? subtotal - cart.discountAmount : subtotal;

  useEffect(() => {
    let cancelled = false;
    const doGetShipping = async() => {
      if (!discountedSubtotal) {
        return;
      }
      const tenant = pickCartTenant(cart);
      try {
        const shippingDetails = myMethod ?
          await ShippingClient.getShipping(myMethod, cart, discountedSubtotal, alwaysVisible, tenant) :
          await ShippingClient.doGetBestShippingEstimate(cart, tenant);
        if (!cancelled) setShipping(shippingDetails);
      } catch (err) {
        // Don't crash the checkout if shipping can't be calculated — the
        // user will see an empty/Unknown shipping option and can pick a
        // pickup or local delivery method instead.
        console.warn('Shipping fetch failed', err);
        if (!cancelled) setShipping({ code: 'ERROR', name: 'Unknown', price: -1 });
      }
    }

    doGetShipping();
    return () => {
      cancelled = true;
    };
  }, [myMethod, cart.deliveryAddress, discountedSubtotal]);

  useEffect(() => {
    if (!alwaysVisible && shipping?.code === "ERROR" && cart.shippingMethod === method) {
      setCartState({
        ...cart,
        shippingMethod: undefined
      });
    }
  }, [alwaysVisible, shipping?.code, cart.shippingMethod, method]);

  if (!alwaysVisible && shipping?.code === "ERROR") {
    return (<></>);
  }

  const usePrice = shipping?.freeShippingThreshold && discountedSubtotal >= shipping.freeShippingThreshold ? 0 : shipping?.price;

  const getShippingName = (ship?: ShippingDetails ): string => {
    if (!ship) {
      return '';
    }
    switch (ship.code) {
      case "LOCAL": return ship.name;
      case "PICKUP": return ship.name;
      default:
        return "Standard shipping";
    }
  }

  const getRadioLabel = () => {
    // Cost is unknown until we have a valid address, so we show only the
    // shipping name on the radio. Per-method price/totals appear elsewhere
    // (TotalsPanel) once the address is provided.
    const label = myMethod === 'PICKUP' ? 'Pick-up' : 'Standard shipping';
    return (
      <div className='shipping-descr'>
        <div className='left'>
          <div>{label}</div>
        </div>
      </div>
    );
  }


  // If there is no name we want a simplified display
  if (!radio) {
    if (shipping === undefined) {
      return (
        <>
        </>
      )
    }
  
    if (usePrice === undefined || usePrice < 0) {
      return (
        <>
        </>
      )
    }

    if (shipping?.freeShippingThreshold) {
      return (
        <>
          <div><Price price={usePrice} showZero={true}/></div>
          <div className='free-shipping'>(Free shipping at <Price price={shipping.freeShippingThreshold}/>)</div>
        </>
      )
    }
    return (
      <>
          <div><Price price={usePrice} showZero={true}/></div>
      </>
    )  
  }

  return (
    <FormControlLabel value={method} control={<Radio />} label={getRadioLabel()}/>
  );
}

export default Shipping